import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { GroupController } from '@dotfile/frontend/shared/components';
import {
  Button,
  Input,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
  VStack,
} from '@dotfile/frontend/shared/design-system';
import { nameFormat } from '@dotfile/shared/common';
import { Individual } from '@dotfile/shared/data-access-client-portal';

import { FormattedTrans } from '../../../../shared';
import {
  useInviteSignerToElectronicSignatureCheck,
  useSendInviteSignerEmail,
} from '../../collaboration/hooks';

type InviteSignerModalContentProps = {
  individual: Pick<Individual, 'id' | 'email' | 'firstName' | 'lastName'>;
  onClose: () => void;
};

const validationSchema = object({
  email: string().email('Invalid email format').required('Email is required'),
});

type FormValues = {
  email: string;
};

export const InviteSignerModalContent = ({
  individual,
  onClose,
}: InviteSignerModalContentProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [inviteSignerToElectronicSignatureCheck] =
    useInviteSignerToElectronicSignatureCheck();
  const [sendInviteSignerEmail] = useSendInviteSignerEmail();

  const methods = useForm<FormValues>({
    defaultValues: {
      email: individual.email || '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values: FormValues) => {
    // Step 1: Save business contact
    const businessContactId = await inviteSignerToElectronicSignatureCheck({
      email: values.email,
      id: individual.id,
    });

    if (!businessContactId) {
      // error toast handled in hook inviteSignerToElectronicSignatureCheck
      return;
    }

    // Step 2: Send collaboration email
    const sent = await sendInviteSignerEmail({
      individualId: businessContactId,
    });

    if (sent) {
      const individualName = nameFormat(
        individual.firstName,
        individual.lastName,
      );
      // Step 3: Show success toast
      toast({
        title: t('collaboration.invite_sent.title', {
          defaultValue: 'Invite sent',
          ns: 'client-portal',
        }),
        description: (
          <FormattedTrans
            i18nKey="collaboration.invite_sent.description"
            defaultValue={`An invitation email has just been sent to <b>${individualName}</b> (${individual.email})`}
            values={{ name: individualName, email: individual.email as string }}
            ns="client-portal"
          />
        ),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    }
  });

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const individualFullName = nameFormat(
    individual.firstName,
    individual.lastName,
  );

  return (
    <FormProvider {...methods}>
      <ModalContent as="form" onSubmit={onSubmit}>
        <ModalHeader>
          {t('checks.electronic_signature.invite.signer_title', {
            defaultValue: 'Invite Signer',
            ns: 'client-portal',
          })}
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text color="black">
              <Trans
                i18nKey="checks.electronic_signature.invite.signer_description"
                ns="client-portal"
                defaults={`Send an invitation to sign the document to <b>${individualFullName}</b>`}
                tOptions={{ individualFullName }}
              />
            </Text>

            <GroupController
              isRequired
              name="email"
              label={t('checks.electronic_signature.invite.email_label', {
                defaultValue: 'Email',
                ns: 'client-portal',
              })}
              control={control}
              render={(field) => (
                <Input
                  {...field}
                  type="email"
                  placeholder={t(
                    'checks.electronic_signature.invite.email_placeholder',
                    {
                      defaultValue: 'Enter email address',
                      ns: 'client-portal',
                    },
                  )}
                />
              )}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={handleCancel} mr={3}>
            {t('common.cancel', {
              defaultValue: 'Cancel',
              ns: 'client-portal',
            })}
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            loadingText={t('checks.electronic_signature.invite.sending', {
              defaultValue: 'Sending...',
              ns: 'client-portal',
            })}
          >
            {t('checks.electronic_signature.invite.send_invitation', {
              defaultValue: 'Send Invitation',
              ns: 'client-portal',
            })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </FormProvider>
  );
};
